import { useCallback } from 'react';

import { useTracking as useTrackingBase } from '@change-corgi/core-react/tracking';

import { useDeferredTrack } from '#src/app/shared/hooks/tracking';
import type { PaymentType } from '#src/app/shared/utils/payments';

type Result = {
	// errorType can be an array of string in case of multiple form validation errors. eg: errorType: ['amount', 'name]
	trackError: ({
		errorMessage,
		errorType,
		paymentType,
	}: {
		errorMessage: string;
		errorType?: string | string[];
		paymentType: PaymentType;
	}) => void;
	trackAmountChanged: ({
		buttonWasClicked,
		buttonClicked,
	}: {
		buttonWasClicked: boolean;
		buttonClicked?: string;
	}) => void;
	trackGeneralError: ({
		paymentType,
		errorMessage,
		errorType,
	}: {
		paymentType: PaymentType;
		errorMessage: string;
		errorType?: string;
	}) => void;
	trackPaymentError: ({
		paymentType,
		errorMessage,
		errorType,
	}: {
		paymentType: PaymentType;
		errorMessage: string;
		errorType?: string;
	}) => void;
	trackPaymentMethodSelected: (paymentType: PaymentType) => void;
	trackSubmit: ({ paymentType }: { paymentType: PaymentType }) => void;
	trackSubmitInvalid: ({ paymentType, errorMessage }: { paymentType: PaymentType; errorMessage: string }) => void;
	trackSubmitValid: ({ paymentType }: { paymentType: PaymentType }) => void;
	trackModelSuccess: ({
		paymentType,
		cardBrand,
		// paymentFirstName,
		// paymentLastName,
		// comboBanditVariant,
		// comboBanditValue,
		// promotedBanditValueShown,
		foundationDonation,
	}: {
		paymentType: PaymentType;
		cardBrand: string;
		// paymentFirstName?: string;
		// paymentLastName?: string;
		// comboBanditVariant?: string;
		// comboBanditValue?: number;
		// promotedBanditValueShown?: number[];
		foundationDonation?: boolean;
	}) => void;
	trackSavedPaymentAuthModalShown: ({ paymentType }: { paymentType: PaymentType }) => void;
	trackPaymentSourceSelected: (paymentType: PaymentType) => void;
	trackEmailInputFocus: () => void;
	trackNameInputFocus: () => void;
	trackFirstNameInputFocus: () => void;
	trackLastNameInputFocus: () => void;
	trackAmountInputFocus: () => void;
	trackCustomAmountSelected: () => void;
};

// eslint-disable-next-line max-lines-per-function
export function useTracking(): Result {
	const track = useTrackingBase();
	const deferredTrack = useDeferredTrack();

	return {
		trackError: useCallback(
			({ errorMessage, errorType, paymentType }) => {
				void track('error', { error_message: errorMessage, error_type: errorType, payment_type: paymentType });
			},
			[track],
		),
		trackAmountChanged: useCallback(
			({ buttonWasClicked, buttonClicked }) => {
				deferredTrack('pp_promotion_amount_changed', {
					button_click: buttonWasClicked,
					button_clicked: buttonClicked,
				});
			},
			[deferredTrack],
		),
		trackGeneralError: useCallback(
			({ paymentType, errorMessage, errorType }) => {
				deferredTrack('pp_general_error', {
					payment_type: paymentType,
					error_message: errorMessage,
					error_type: errorType,
				});
			},
			[deferredTrack],
		),
		trackPaymentError: useCallback(
			({ paymentType, errorMessage, errorType }) => {
				deferredTrack('pp_payment_error', {
					payment_type: paymentType,
					error_message: errorMessage,
					error_type: errorType,
				});
			},
			[deferredTrack],
		),
		trackPaymentMethodSelected: useCallback(
			(paymentType: PaymentType) => {
				deferredTrack('pp_select_payment_type', { payment_type: paymentType });
			},
			[deferredTrack],
		),
		trackSubmit: useCallback(
			({ paymentType }) => {
				deferredTrack('pp_submit', { payment_type: paymentType });
			},
			[deferredTrack],
		),
		trackSubmitInvalid: useCallback(
			({ paymentType, errorMessage }) => {
				deferredTrack('pp_submit_invalid', { payment_type: paymentType, error_message: errorMessage });
			},
			[deferredTrack],
		),
		trackSubmitValid: useCallback(
			({ paymentType }) => {
				void track('pp_submit_valid', { payment_type: paymentType });
			},
			[track],
		),
		trackModelSuccess: useCallback(
			({ paymentType, cardBrand, foundationDonation }) => {
				void track('pp_model_success', {
					payment_type: paymentType,
					card_type: cardBrand,
					...{ foundation_donation: foundationDonation ?? false },
				});
			},
			[track],
		),
		trackSavedPaymentAuthModalShown: useCallback(
			({ paymentType }) => {
				void track('pp_saved_payment_auth_modal_show', { payment_type: paymentType });
			},
			[track],
		),
		trackPaymentSourceSelected: useCallback(
			(paymentType: PaymentType) => {
				deferredTrack('pp_select_payment_source', { payment_type: paymentType });
			},
			[deferredTrack],
		),

		trackEmailInputFocus: useCallback(() => {
			void track('pp_focus_email');
		}, [track]),

		trackNameInputFocus: useCallback(() => {
			void track('pp_focus_name');
		}, [track]),

		trackFirstNameInputFocus: useCallback(() => {
			void track('pp_focus_first_name');
		}, [track]),

		trackLastNameInputFocus: useCallback(() => {
			void track('pp_focus_last_name');
		}, [track]),
		trackAmountInputFocus: useCallback(() => {
			void track('pp_focus_amount');
		}, [track]),
		trackCustomAmountSelected: useCallback(() => {
			deferredTrack('pp_click_custom_amount');
		}, [deferredTrack]),
	};
}
